<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import Loading from '@/components/ui/Loading.vue'
import Counter from '@/components/ui/Counter.vue'
import { ref, computed, onMounted } from 'vue'
import { pinia } from '@/stores'
import { useRouterStore } from '@/stores/router'
import { useLang } from '@/composables/use-lang'
import PaymentContent from '@/lang/payment.json'
import { createRemoteDiagnose } from '@/services/remote-diagnose'
import { setStore } from '@/utils/remote-diagnose'
import { storeToRefs } from 'pinia'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { cleanQueryString } from '@/utils/router'
import { getLimitDate, getTimeLeft, removeLimitDate, setLimitDate } from '@/utils/limit-date'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  },
  stripe_key: {
    type: String,
    default: ''
  }
})

const { push, resetParams } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)
const { changePaymentMethods } = remoteDiagnoseStore
const { state } = storeToRefs(remoteDiagnoseStore)

// Stripe acts as the iDEAL backup gateway. Offer it as a fallback only when configured.
const stripeFallbackAvailable = computed<boolean>(() => !!props.stripe_key)

const loading = ref<boolean>(true)
const error = ref<boolean>(false)
const redirectUrl = ref<string>('')
const emit = defineEmits(['scroll'])

const langContent = useLang(PaymentContent)

const goAddress = (): void => {
  push('address')
}

const goPayment = (): void => {
  window.location.href = redirectUrl.value
}

// Route the user through Stripe (iDEAL) as a backup when the direct iDEAL gateway fails.
const goStripeFallback = (): void => {
  changePaymentMethods(true, true)
  push('payment-stripe')
}

onMounted(async () => {
  emit('scroll')
  cleanQueryString()

  // validate if the time already expired and redirect it to timesup page
  const limitDate = getLimitDate()
  console.log({ limitDate })
  if (limitDate) {
    const timeLeft = getTimeLeft(limitDate)
    console.log({ timeLeft })
    if (timeLeft <= 0) {
      removeLimitDate()
      push('timesup')
      return null
    }
  }

  const returnUrl = window.location.href + `?redirect_status=succeeded&validate_payment=true`

  if (!state.value.phone_number) {
    loading.value = false
    removeLimitDate()
    push('home')
  }
  resetParams()
  const result = await createRemoteDiagnose({ ...state.value, returnUrl }, props.aws_url)

  console.log({ result })

  if (!result.redirectUrl) {
    error.value = true
    loading.value = false
    removeLimitDate()
    return null
  }

  redirectUrl.value = result.redirectUrl
  error.value = false

  //set current state in local storage
  setStore(state.value)
  loading.value = false

  setLimitDate(result.created_at)
})
</script>

<template>
  <div v-if="langContent" class="max-w-lg mx-auto">
    <div
      v-if="error"
      class="rounded-xl border border-english-vermillion/30 bg-english-vermillion/5 p-8 text-center"
    >
      <div
        class="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-english-vermillion/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-7 w-7 text-english-vermillion"
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h2 class="mb-2 text-lg font-semibold text-dark-liver">{{ langContent.error_title }}</h2>
      <p class="mb-6 text-sm text-dark-liver">{{ langContent.error_message }}</p>
      <app-button class="mx-auto" @click="goAddress">{{ langContent.error_button }}</app-button>
      <button
        v-if="stripeFallbackAvailable"
        type="button"
        class="mt-6 block w-full text-sm text-dark-liver"
        @click="goStripeFallback"
      >
        {{ langContent.payment_problem_text }}
        <span class="font-semibold text-english-vermillion underline hover:text-sweet-brown">{{
          langContent.payment_problem_link
        }}</span>
      </button>
    </div>
    <Loading v-if="loading && !error" />
    <div v-if="!loading && !error">
      <counter v-if="!loading" />
      <div>
        <h2 class="text-center">{{ langContent.idea_payment_title }}</h2>
        <p class="text-center">{{ langContent.idea_payment_message }}</p>
      </div>
      <div v-if="!loading" class="flex justify-evenly">
        <app-button
          type="button"
          @click="goAddress"
          class="mt-10 bg-white border border-english-vermillion !text-english-vermillion hover:bg-sweet-brown hover:!text-white"
          >Cancel</app-button
        >
        <app-button v-if="redirectUrl" id="submit" type="submit" class="mt-10" @click="goPayment"
          >Confirm</app-button
        >
      </div>
    </div>
  </div>
</template>
